// Billing.tsx — /billing route.
// Membership summary at top (status + balance + trial-days-left OR renewal date),
// followed by a chronological token ledger (grants, spends, refunds) for the
// signed-in user. Reads token_ledger directly via Supabase JS — RLS limits the
// rows to the caller's user_id (see migration 0001 token_ledger_select_self).

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { supabase } from '../auth/supabase';
import { openCustomerPortal, PortalError } from '../utils/checkout';
import {
  listDeviceTokens,
  revokeDeviceToken,
  type DeviceTokenRow,
} from '../utils/deviceTokens';
import {
  membershipBadge,
  membershipDetail,
  formatPlan,
  type LedgerRow,
  sourceLabel,
} from '../auth/billing';

export default function Billing() {
  const { user, membership, balance, loading: authLoading, refresh } = useAuth();
  const [rows, setRows] = useState<LedgerRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [portalBusy, setPortalBusy] = useState(false);
  const [portalNotice, setPortalNotice] = useState<{ kind: 'info' | 'error'; message: string } | null>(null);
  const [devices, setDevices] = useState<DeviceTokenRow[] | null>(null);
  const [deviceError, setDeviceError] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('token_ledger')
        .select('id, delta, source, note, created_at')
        .order('created_at', { ascending: false });
      if (cancelled) return;
      if (error) {
        setError(error.message);
        return;
      }
      setRows((data ?? []) as LedgerRow[]);
    })();
    return () => { cancelled = true; };
  }, [user]);

  const loadDevices = useCallback(async () => {
    try {
      setDeviceError(null);
      const list = await listDeviceTokens();
      setDevices(list);
    } catch (e) {
      setDeviceError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    loadDevices();
  }, [user, loadDevices]);

  async function handleRevoke(id: string) {
    if (!confirm('Revoke this workstation? ConstructiVision-CAD on that machine will need to sign in again.')) return;
    try {
      setRevokingId(id);
      await revokeDeviceToken(id);
      await loadDevices();
    } catch (e) {
      setDeviceError(e instanceof Error ? e.message : String(e));
    } finally {
      setRevokingId(null);
    }
  }

  // Running balance: ledger is sorted DESC; running[i] = balance after row[i] applied.
  const rowsWithRunning = useMemo(() => {
    if (!rows) return null;
    const total = rows.reduce((sum, r) => sum + r.delta, 0);
    let running = total;
    return rows.map(r => {
      const after = running;
      running -= r.delta;
      return { ...r, balanceAfter: after };
    });
  }, [rows]);

  if (authLoading) {
    return <main className="billing-page"><p>Loading…</p></main>;
  }

  if (!user) {
    return (
      <main className="billing-page">
        <h1>Billing</h1>
        <p>Sign in to see your membership and token history.</p>
        <p><a href={`${import.meta.env.BASE_URL}`}>Back to ConstructiVision</a></p>
      </main>
    );
  }

  const badge = membershipBadge(membership);
  const detail = membershipDetail(membership);
  const planLabel = formatPlan(membership?.plan ?? null);

  async function handlePortal() {
    setPortalBusy(true);
    setPortalNotice(null);
    try {
      await openCustomerPortal();
    } catch (err) {
      if (err instanceof PortalError && err.code === 'no_customer') {
        setPortalNotice({
          kind: 'info',
          message: "You don't have a paid subscription yet. Buy a plan first — there's nothing to manage in the Stripe portal until then.",
        });
      } else {
        const msg = err instanceof Error ? err.message : String(err);
        setPortalNotice({ kind: 'error', message: `Couldn't open Stripe portal: ${msg}` });
      }
      setPortalBusy(false);
    }
  }

  return (
    <main className="billing-page">
      <header className="billing-header">
        <h1>Billing</h1>
        <a className="billing-back" href={import.meta.env.BASE_URL}>← Back to ConstructiVision</a>
      </header>

      <section className="billing-summary">
        <div className="billing-summary-card">
          <div className="billing-summary-label">Membership</div>
          <div className="billing-summary-value">
            <span className={`billing-badge billing-badge-${badge.kind}`}>{badge.label}</span>
          </div>
          {planLabel && <div className="billing-summary-sub">{planLabel}</div>}
          {detail && <div className="billing-summary-sub">{detail}</div>}
        </div>

        <div className="billing-summary-card">
          <div className="billing-summary-label">Panel credits</div>
          <div className="billing-summary-value billing-balance">{balance}</div>
          <div className="billing-summary-sub">1 credit per panel print or export</div>
        </div>

        <div className="billing-summary-actions">
          <a className="billing-btn billing-btn-primary" href={`${import.meta.env.BASE_URL}pricing`}>
            Buy more credits
          </a>
          {membership && membership.plan && (
            <button
              className="billing-btn"
              onClick={handlePortal}
              disabled={portalBusy}
            >
              {portalBusy ? 'Opening…' : 'Manage subscription'}
            </button>
          )}
          <button className="billing-btn billing-btn-ghost" onClick={() => refresh()}>
            Refresh
          </button>
        </div>
        {portalNotice && (
          <p className={`billing-notice billing-notice-${portalNotice.kind}`}>{portalNotice.message}</p>
        )}
      </section>

      <section className="billing-history">
        <h2>Authorized workstations</h2>
        <p className="billing-empty" style={{ marginTop: 0 }}>
          ConstructiVision-CAD installs you've signed in from. Revoke a workstation if you've lost the machine
          or want to force a fresh sign-in. The token is bound to a specific machine ID — revoking here is
          immediate; the next print or export from that workstation will require re-authorization.
        </p>
        {deviceError && <p className="billing-error">Couldn't load workstations: {deviceError}</p>}
        {devices === null && !deviceError && <p>Loading…</p>}
        {devices && devices.length === 0 && (
          <p className="billing-empty">No workstations authorized yet. Sign in from ConstructiVision-CAD to add one.</p>
        )}
        {devices && devices.length > 0 && (
          <table className="billing-table">
            <thead>
              <tr>
                <th>Label</th>
                <th>Machine ID</th>
                <th>Last seen</th>
                <th>Authorized</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {devices.map(d => (
                <tr key={d.id}>
                  <td>{d.label ?? <span className="billing-empty">(no label)</span>}</td>
                  <td className="billing-note" style={{ fontFamily: 'monospace', fontSize: 12 }}>{d.machine_id}</td>
                  <td>{d.last_seen_at ? new Date(d.last_seen_at).toLocaleString() : '—'}</td>
                  <td>{new Date(d.created_at).toLocaleDateString()}</td>
                  <td>
                    {d.revoked_at
                      ? <span className="billing-empty">Revoked {new Date(d.revoked_at).toLocaleDateString()}</span>
                      : <span className="billing-credit">Active</span>}
                  </td>
                  <td>
                    {!d.revoked_at && (
                      <button
                        className="billing-btn billing-btn-ghost"
                        onClick={() => handleRevoke(d.id)}
                        disabled={revokingId === d.id}
                      >
                        {revokingId === d.id ? 'Revoking…' : 'Revoke'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="billing-history">
        <h2>History</h2>
        {error && <p className="billing-error">Couldn't load history: {error}</p>}
        {rowsWithRunning === null && !error && <p>Loading…</p>}
        {rowsWithRunning && rowsWithRunning.length === 0 && (
          <p className="billing-empty">No activity yet. Your first 10 trial credits should appear here once your account is provisioned.</p>
        )}
        {rowsWithRunning && rowsWithRunning.length > 0 && (
          <table className="billing-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Activity</th>
                <th className="billing-num">Change</th>
                <th className="billing-num">Balance</th>
                <th>Note</th>
              </tr>
            </thead>
            <tbody>
              {rowsWithRunning.map(r => (
                <tr key={r.id}>
                  <td>{new Date(r.created_at).toLocaleString()}</td>
                  <td>{sourceLabel(r.source)}</td>
                  <td className={`billing-num ${r.delta > 0 ? 'billing-credit' : 'billing-debit'}`}>
                    {r.delta > 0 ? `+${r.delta}` : r.delta}
                  </td>
                  <td className="billing-num">{r.balanceAfter}</td>
                  <td className="billing-note">{r.note ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </main>
  );
}
